import React, { useState, useEffect } from 'react';
import {
  Modal,
  Row,
  Col,
  Card,
  InputNumber,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Alert,
  Divider
} from 'antd';
import { ShoppingCartOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { customerOrderAPI } from '../services/api.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';

const { Title, Text } = Typography;

interface InventoryStock {
  id: number;
  product_id: number;
  stock_code?: string;
  date_stock?: string;
  entry_date?: string;
  customer_id?: number;
  customer_company?: string;
  qta_available: number;
  qta_reserved: number;
  qta_in_production: number;
  qta_in_transit: number;
  supplier_order_status?: string;
  expected_availability_date?: string;
  cost?: number;
  price?: number;
  // Prenotazioni totali (tutti gli ordini)
  available_reservations: number;
  in_transit_reservations: number;
  in_production_reservations: number;
  // Prenotazioni specifiche di questo ordine
  current_order_available_reservations: number;
  current_order_in_transit_reservations: number;
  current_order_in_production_reservations: number;
}

interface MultiSourceReservationModalProps {
  visible: boolean;
  onClose: () => void;
  onReservationComplete: () => void;
  stock: InventoryStock | null;
  productName: string;
  orderId: number;
  requestedQuantity?: number;
  productTotalReserved?: number;
  orderUnitPrice?: number;
  /** Cliente dell'ordine: se diverso dal cliente della giacenza, avviso + conferma */
  orderCustomerId?: number;
  orderCustomerName?: string;
}

const MultiSourceReservationModal: React.FC<MultiSourceReservationModalProps> = ({
  visible,
  onClose,
  onReservationComplete,
  stock,
  productName,
  orderId,
  requestedQuantity,
  productTotalReserved,
  orderUnitPrice,
  orderCustomerId,
  orderCustomerName
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [reservations, setReservations] = useState({
    available: 0,
    in_transit: 0,
    in_production: 0
  });
  const [originalReservations, setOriginalReservations] = useState({
    available: 0,
    in_transit: 0,
    in_production: 0
  });

  // Inizializza prenotazioni esistenti quando il modal si apre
  useEffect(() => {
    if (visible && stock) {
      initializeReservations();
    }
  }, [visible, stock]);

  const initializeReservations = () => {
    if (!stock) return;
    
    // Usa i dati delle prenotazioni già presenti nello stock
    const currentReservations = {
      available: stock.current_order_available_reservations || 0,
      in_transit: stock.current_order_in_transit_reservations || 0,
      in_production: stock.current_order_in_production_reservations || 0
    };
    
    setReservations(currentReservations);
    setOriginalReservations(currentReservations);
  };

  const resetForm = () => {
    setReservations({
      available: 0,
      in_transit: 0,
      in_production: 0
    });
    setOriginalReservations({
      available: 0,
      in_transit: 0,
      in_production: 0
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!stock) return null;

  // Calcola le quantità massime prenotabili per ogni fonte
  // Formula: Differenza (disponibile per altri) + PrenotazioniQuestoOrdine
  // Dove Differenza = TotaleStock - PrenotazioniTotali
  const availableDifference = Math.max(0, stock.qta_available - stock.available_reservations);
  const maxAvailable = availableDifference + (stock.current_order_available_reservations || 0);
  
  // Per In Transito: usa sempre qta_in_transit
  const inTransitQuantity = stock.qta_in_transit || 0;
  const inTransitDifference = Math.max(0, inTransitQuantity - stock.in_transit_reservations);
  const maxInTransit = inTransitDifference + (stock.current_order_in_transit_reservations || 0);
  
  // Per In Produzione: usa sempre qta_in_production
  const inProductionQuantity = stock.qta_in_production || 0;
  const inProductionDifference = Math.max(0, inProductionQuantity - stock.in_production_reservations);
  const maxInProduction = inProductionDifference + (stock.current_order_in_production_reservations || 0);

  const totalReservationRequested = reservations.available + reservations.in_transit + reservations.in_production;
  const hasValidReservations = totalReservationRequested > 0;

  // Calcola il budget rimanente per questo prodotto nell'ordine
  // productTotalReserved include le prenotazioni dello stock corrente, quindi le sottraiamo per avere il totale degli ALTRI stock
  const currentStockOriginalTotal = originalReservations.available + originalReservations.in_transit + originalReservations.in_production;
  const otherStocksReserved = (productTotalReserved || 0) - currentStockOriginalTotal;
  const orderBudget = requestedQuantity !== undefined && requestedQuantity > 0
    ? Math.max(0, requestedQuantity - otherStocksReserved)
    : Infinity;
  const exceedsOrderLimit = requestedQuantity !== undefined && requestedQuantity > 0 && totalReservationRequested > orderBudget;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT');
    } catch {
      return dateString;
    }
  };

  const handleReservationChange = (source: 'available' | 'in_transit' | 'in_production', value: number | null) => {
    setReservations(prev => ({
      ...prev,
      [source]: value || 0
    }));
  };

  const handleConfirmReservation = async () => {
    // Controlla se ci sono modifiche rispetto alle prenotazioni originali
    const hasChanges = 
      reservations.available !== originalReservations.available ||
      reservations.in_transit !== originalReservations.in_transit ||
      reservations.in_production !== originalReservations.in_production;

    if (!hasChanges) {
      message.info('Nessuna modifica alle prenotazioni');
      handleClose();
      return;
    }

    setLoading(true);
    try {
      await submitReservations(false);
    } finally {
      setLoading(false);
    }
  };

  const submitReservations = async (forceCrossCustomer: boolean) => {
    try {
      // Utilizza la nuova API di aggiornamento prenotazioni
      const result = await customerOrderAPI.updateProductReservations(
        orderId,
        stock.product_id,
        stock.id,
        {
          available: reservations.available,
          in_transit: reservations.in_transit,
          in_production: reservations.in_production,
          unit_price: orderUnitPrice || stock.price || 0,
          force_cross_customer: forceCrossCustomer || undefined
        }
      );

      const changes = result.changes || [];
      if (changes.length > 0) {
        message.success(`Prenotazioni aggiornate: ${changes.join(', ')}`);
      } else {
        message.success('Prenotazioni aggiornate con successo');
      }

      onReservationComplete();
      handleClose();

    } catch (error: any) {
      // 409 = giacenza dedicata a un altro cliente: chiedi conferma e riprova col flag
      if (error.response?.status === 409 && !forceCrossCustomer) {
        Modal.confirm({
          title: 'Giacenza dedicata a un altro cliente',
          content: error.response?.data?.detail,
          okText: 'Prenota comunque',
          okButtonProps: { danger: true },
          cancelText: 'Annulla',
          onOk: () => submitReservations(true),
        });
        return;
      }
      console.error('Errore nell\'aggiornamento prenotazioni:', error);
      const errorMessage = error.response?.data?.detail || 'Errore nell\'aggiornamento prenotazioni';
      message.error(errorMessage);
    }
  };

  return (
    <Modal
      title={
        <Space direction="vertical" size="small">
          <div>
            <ShoppingCartOutlined style={{ marginRight: 8 }} />
            Prenota Prodotti
          </div>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {productName} - Stock: {stock.stock_code || 'N/A'}
            {stock.cost != null && <> | Costo: € {formatCurrency(stock.cost)}</>}
            {stock.entry_date && <> | Ingresso: {formatDate(stock.entry_date)}</>}
          </Text>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={
        <Space>
          <Button onClick={handleClose} disabled={loadingReservations}>
            Annulla
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleConfirmReservation}
            loading={loading || loadingReservations}
            disabled={loadingReservations || exceedsOrderLimit}
          >
            Aggiorna Prenotazioni ({totalReservationRequested})
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: '16px' }}>
        {requestedQuantity !== undefined && requestedQuantity > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <Space wrap>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                Quantità richiesta dall'ordine: <strong>{requestedQuantity}</strong>
              </Tag>
              {productTotalReserved !== undefined && (
                <>
                  <Tag color={productTotalReserved > 0 ? 'green' : 'default'} style={{ fontSize: '14px', padding: '4px 12px' }}>
                    Già prenotato (tutti gli stock): <strong>{productTotalReserved}</strong>
                  </Tag>
                  <Tag
                    color={requestedQuantity - productTotalReserved > 0 ? 'red' : 'green'}
                    style={{ fontSize: '14px', padding: '4px 12px' }}
                  >
                    {requestedQuantity - productTotalReserved > 0
                      ? <>Ancora da prenotare: <strong>{requestedQuantity - productTotalReserved}</strong></>
                      : <strong>Completo</strong>
                    }
                  </Tag>
                </>
              )}
            </Space>
          </div>
        )}
        {stock.customer_id != null && orderCustomerId != null && stock.customer_id !== orderCustomerId && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Giacenza dedicata a un altro cliente"
            description={`Questa giacenza è dedicata a ${stock.customer_company || 'un altro cliente'}, mentre l'ordine è di ${orderCustomerName || 'un cliente diverso'}. La prenotazione richiederà una conferma esplicita.`}
          />
        )}
        <Text>
          Modifica le prenotazioni esistenti per questo prodotto.
          Le quantità mostrate includono le prenotazioni già effettuate per questo ordine.
        </Text>
        {loadingReservations && (
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <Text type="secondary">Caricamento prenotazioni esistenti...</Text>
          </div>
        )}
      </div>

      <Row gutter={[16, 16]}>
        {/* Colonna Disponibile */}
        <Col span={8}>
          <Card 
            title={<Text strong style={{ color: '#52c41a' }}>Disponibile</Text>}
            size="small"
            style={{ height: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <Tag color="green" style={{ fontSize: '16px', padding: '4px 12px' }}>
                  {stock.available_reservations > 0 
                    ? `${stock.qta_available} (di cui Opz. ${stock.available_reservations})`
                    : stock.qta_available.toString()
                  }
                </Tag>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Max prenotabile: <Text strong>{maxAvailable}</Text>
                </div>
              </div>
              
              <Divider style={{ margin: '12px 0' }} />
              
              <div>
                <Text>Quantità da prenotare:</Text>
                <InputNumber
                  value={reservations.available}
                  onChange={(value) => handleReservationChange('available', value)}
                  min={0}
                  max={Math.min(maxAvailable, Math.max(0, orderBudget - reservations.in_transit - reservations.in_production))}
                  style={{ width: '100%', marginTop: '8px' }}
                  placeholder="0"
                  disabled={maxAvailable === 0 && originalReservations.available === 0}
                />
                {maxAvailable === 0 && originalReservations.available === 0 && (
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Nessuna disponibilità
                  </Text>
                )}
              </div>
              
              <div style={{ marginTop: '8px' }}>
                <Text strong style={{ color: '#52c41a' }}>
                  Disponibilità: Immediata
                </Text>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Colonna In Transito */}
        <Col span={8}>
          <Card 
            title={<Text strong style={{ color: '#fa8c16' }}>In Transito</Text>}
            size="small"
            style={{ height: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <Tag color="orange" style={{ fontSize: '16px', padding: '4px 12px' }}>
                  {stock.in_transit_reservations > 0 
                    ? `${inTransitQuantity} (di cui Opz. ${stock.in_transit_reservations})`
                    : inTransitQuantity.toString()
                  }
                </Tag>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Max prenotabile: <Text strong>{maxInTransit}</Text>
                </div>
              </div>
              
              <Divider style={{ margin: '12px 0' }} />
              
              <div>
                <Text>Quantità da prenotare:</Text>
                <InputNumber
                  value={reservations.in_transit}
                  onChange={(value) => handleReservationChange('in_transit', value)}
                  min={0}
                  max={Math.min(maxInTransit, Math.max(0, orderBudget - reservations.available - reservations.in_production))}
                  style={{ width: '100%', marginTop: '8px' }}
                  placeholder="0"
                  disabled={maxInTransit === 0 && originalReservations.in_transit === 0}
                />
                {maxInTransit === 0 && originalReservations.in_transit === 0 && (
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Nessuna disponibilità
                  </Text>
                )}
              </div>
              
              <div style={{ marginTop: '8px' }}>
                <Text strong style={{ color: '#fa8c16' }}>
                  Arrivo previsto: {formatDate(stock.expected_availability_date)}
                </Text>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Colonna In Produzione */}
        <Col span={8}>
          <Card 
            title={<Text strong style={{ color: '#1890ff' }}>In Produzione</Text>}
            size="small"
            style={{ height: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <Tag color="blue" style={{ fontSize: '16px', padding: '4px 12px' }}>
                  {stock.in_production_reservations > 0 
                    ? `${inProductionQuantity} (di cui Opz. ${stock.in_production_reservations})`
                    : inProductionQuantity.toString()
                  }
                </Tag>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Max prenotabile: <Text strong>{maxInProduction}</Text>
                </div>
              </div>
              
              <Divider style={{ margin: '12px 0' }} />
              
              <div>
                <Text>Quantità da prenotare:</Text>
                <InputNumber
                  value={reservations.in_production}
                  onChange={(value) => handleReservationChange('in_production', value)}
                  min={0}
                  max={Math.min(maxInProduction, Math.max(0, orderBudget - reservations.available - reservations.in_transit))}
                  style={{ width: '100%', marginTop: '8px' }}
                  placeholder="0"
                  disabled={maxInProduction === 0 && originalReservations.in_production === 0}
                />
                {maxInProduction === 0 && originalReservations.in_production === 0 && (
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Nessuna disponibilità
                  </Text>
                )}
              </div>
              
              <div style={{ marginTop: '8px' }}>
                <Text strong style={{ color: '#1890ff' }}>
                  Consegna prevista: {formatDate(stock.expected_availability_date)}
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Warning limite ordine raggiunto */}
      {requestedQuantity !== undefined && requestedQuantity > 0 && orderBudget !== Infinity && totalReservationRequested >= orderBudget && !exceedsOrderLimit && (
        <Alert
          style={{ marginTop: '16px' }}
          message="Limite raggiunto"
          description={`Hai raggiunto il limite massimo prenotabile per questo prodotto (${requestedQuantity} richiesti dall'ordine).`}
          type="warning"
          showIcon
        />
      )}

      {exceedsOrderLimit && (
        <Alert
          style={{ marginTop: '16px' }}
          message="Quantità eccedente"
          description={`Il totale prenotato (${totalReservationRequested}) supera la quantità richiesta dall'ordine (${requestedQuantity}). Riduci le quantità.`}
          type="error"
          showIcon
        />
      )}

      {/* Riepilogo */}
      {totalReservationRequested > 0 && !exceedsOrderLimit && (
        <Alert
          style={{ marginTop: '16px' }}
          message="Riepilogo Prenotazioni"
          description={
            <div>
              <div>Totale quantità da prenotare: <Text strong>{totalReservationRequested}</Text>{orderBudget !== Infinity && ` / ${orderBudget} max`}</div>
              {reservations.available > 0 && <div>• Da Disponibile: {reservations.available}</div>}
              {reservations.in_transit > 0 && <div>• Da In Transito: {reservations.in_transit}</div>}
              {reservations.in_production > 0 && <div>• Da In Produzione: {reservations.in_production}</div>}
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">
                  Le prenotazioni saranno create automaticamente dal sistema.
                </Text>
              </div>
            </div>
          }
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

export default MultiSourceReservationModal;